// Layout slot manifest shape. Each layout declares the slots it owns so the
// dashboard's slot-mapping editor knows which slot ids are valid for this
// layout, which block kinds each slot accepts, and what to call them in the UI.
//
// The manifest is *data about the layout*, separate from the layout's React
// component. The server reads from a generated artifact (see the build target
// downstream) and feeds the editor — the operator never has to remember slot
// ids.

import type { BlockKind } from "../blocks/types";

/** Which viewport a slot feeds. Declared by the layout (not operator-editable):
 *  in single-menu the desktop side panel and the mobile bottom sheet are
 *  different regions, so their slots are tagged "desktop" / "mobile"; shared
 *  regions (notifications, modals, series picker) are "both". The customize UI
 *  groups slots by this so it's clear which menu each slot belongs to. */
export type SlotTarget = "both" | "desktop" | "mobile";

export interface SlotManifest {
  /** Stable id the layout's <Slot slotId="..."> calls and the SlotMapping JSON use. */
  id: string;
  /** Human label for the editor. */
  label: string;
  /** One-line description shown as helper text in the editor. */
  description?: string;
  /** Block kinds this slot will render. Anything else is a no-op (Slot.tsx
   *  logs + skips), but the editor uses this list to filter the "add block"
   *  picker so the operator only sees relevant kinds. */
  accepts: readonly BlockKind[];
  /** Viewport this slot feeds. Defaults to "both" when omitted. Authoring
   *  metadata only — the layout's own render gates which region shows where;
   *  this drives how the editor groups slots. */
  target?: SlotTarget;
}

export interface LayoutManifest {
  /** Layout id — matches Designer.LayoutTemplate slugs. */
  id: string;
  slots: readonly SlotManifest[];
}

/** Common block-kind shorthands so manifest files stay readable. */
export const ALL_KINDS: readonly BlockKind[] = [
  "action", "text", "stat", "image", "material-collections",
  "selection-surfaces", "catalog", "cart-items", "series-image", "series-name", "series-description", "divider", "line", "spacer", "group",
  "input-text", "input-email", "input-textarea", "form-submit",
  "product-table", "product-image", "product-name", "product-parameter", "product-sku", "product-quantity", "product-materials", "product-price",
];
/** Cart-body slot: the composable product table plus chrome. The product-* row
 *  kinds live inside the table's columns, but they're listed here so the editor
 *  surfaces them when authoring a column's cell blocks. */
export const PRODUCT_TABLE_KINDS: readonly BlockKind[] = [
  "product-table", "product-image", "product-name", "product-parameter", "product-sku", "product-quantity", "product-materials", "product-price",
  "text", "image", "divider", "line", "spacer", "group",
];
/** The notifications slot only takes notification blocks. */
export const NOTIFICATION_KINDS: readonly BlockKind[] = ["notification"];
/** Kinds that make sense inside the on-selection popup — selection-surfaces
 *  plus the chrome (text + line + spacer). Action/group kept on so an
 *  operator can drop a delete button or pad the layout if they want. */
export const SELECTION_KINDS: readonly BlockKind[] = [
  "selection-surfaces", "text", "action", "divider", "line", "spacer", "group",
];
/** Toolbar-style slots accept only buttons + the divider/line/spacer cosmetics. */
export const ACTIONS_ONLY: readonly BlockKind[] = ["action", "divider", "line", "spacer", "group"];
/** Card composites: same as actions-only plus stats + text. */
export const CARD_KINDS: readonly BlockKind[] = [
  "stat", "text", "action", "divider", "line", "spacer", "group", "image",
];
/** Form composites: input controls + the submit button + chrome. Used by
 *  modal slots that exist to collect operator data. */
export const FORM_KINDS: readonly BlockKind[] = [
  "input-text", "input-email", "input-textarea", "form-submit",
  "text", "divider", "line", "spacer", "group",
];
